import { useMemo } from "react";

// Third party
import { AnimatePresence, motion } from "framer-motion";

// MUI
import { Stack } from "@mui/material";

// Components
import ConfiguredAccount from "./ConfiguredAccount";

// Types
import { IConfiguredAccount } from "@/Settings/types/administrationTools/model";

enum AccountStatus {
	AWAITING = "AWAITING",
	AUTHENTICATED = "AUTHENTICATED",
}

// Animation variants
const containerVariants = {
	hidden: { opacity: 0 },
	visible: {
		opacity: 1,
		transition: {
			staggerChildren: 0.1,
			delayChildren: 0.05,
		},
	},
};

const itemVariants = {
	hidden: {
		opacity: 0,
		y: 20,
		scale: 0.95,
	},
	visible: {
		opacity: 1,
		y: 0,
		scale: 1,
		transition: {
			duration: 0.3,
			ease: "easeOut",
		},
	},
	exit: {
		opacity: 0,
		y: -20,
		scale: 0.95,
		transition: {
			duration: 0.25,
			ease: "easeIn",
		},
	},
};

interface IConfiguredAccountsListProps {
	providerName: string;
	configuredAccounts: IConfiguredAccount[];
	timezone: string;
	index: number;
	onDeleteAwaitingAccount: (providerName: string, displayId: string) => void;
}

const ConfiguredAccountsList = (props: IConfiguredAccountsListProps) => {
	const { providerName, configuredAccounts, timezone, index, onDeleteAwaitingAccount } = props;

	const { awaitingAccounts, authenticatedAccounts } = useMemo(() => {
		const awaiting =
			configuredAccounts
				?.filter((account) => account.status === AccountStatus.AWAITING)
				.sort((a, b) => a.statusDate.localeCompare(b.statusDate)) || [];
		const authenticated =
			configuredAccounts
				?.filter((account) => account.status !== AccountStatus.AWAITING)
				.sort((a, b) => a.statusDate.localeCompare(b.statusDate)) || [];

		return {
			awaitingAccounts: awaiting,
			authenticatedAccounts: authenticated,
		};
	}, [configuredAccounts]);

	const renderAccountsList = (accounts: IConfiguredAccount[], accountStatus: AccountStatus) => {
		if (!accounts.length) {
			return null;
		}

		return accounts.map((account, idx) => {
			const keyIndex = `${accountStatus}Account-${index}-${idx}`;
			return (
				<motion.div key={keyIndex} variants={itemVariants} initial="hidden" animate="visible" exit="exit" layout>
					<ConfiguredAccount
						providerName={providerName}
						account={account}
						onDelete={onDeleteAwaitingAccount}
						timezone={timezone}
					/>
				</motion.div>
			);
		});
	};

	if (!awaitingAccounts.length && !authenticatedAccounts.length) {
		return null;
	}

	return (
		<motion.div variants={containerVariants} initial="hidden" animate="visible">
			<Stack display={"flex"} direction={"column"}>
				<AnimatePresence mode="popLayout">{renderAccountsList(awaitingAccounts, AccountStatus.AWAITING)}</AnimatePresence>
				<AnimatePresence mode="popLayout">{renderAccountsList(authenticatedAccounts, AccountStatus.AUTHENTICATED)}</AnimatePresence>
			</Stack>
		</motion.div>
	);
};

export default ConfiguredAccountsList;
